import {
  Row,
  Col,
  Typography,
  Table,
  Button,
  message,
  Descriptions,
} from "antd";
import { routes } from "common/routes";
import { generateHeaders, getSlotName } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  cancelStoreSecPicking,
  generateDownloadUrl,
  removeProcessingRequisitionUserMapping,
  saveStoreSecPicking,
} from "store/requisition/requisitionSlice";

const MESSAGE_KEY = "SAVE_STORE_SEC_PICKING";

export const SaveStoreSecondaryPickingPage = () => {
  useDocumentTitle("Requisition: Save Store Sec. Picking");
  const { processingStoreSecPicking, status, error, slots } =
    useAppSelector((state) => state.requisition);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const { timeUploaded } = processingStoreSecPicking;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;

      if (minsPassed >= 15) {
        dispatch(removeProcessingRequisitionUserMapping());
      }
    }

    if (!Object.keys(processingStoreSecPicking).length) {
      navigate(`/${routes.STORE_REQUISITION.SEC_PICKING_UPLOAD}`);
    }
  }, [dispatch, processingStoreSecPicking, navigate]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "saving sec. picking", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Store secondary picking saved!",
        duration: 5,
        key: MESSAGE_KEY,
      });
    } else if (status === "cancelling") {
      message.loading({ content: "Cancelling...", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error]);

  let content;
  const { headerMapping } = processingStoreSecPicking;
  const { Text } = Typography;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMapping),
    [headerMapping]
  );

  const onSaveUserMapping = () => {
    dispatch(saveStoreSecPicking());
  };

  const onCancelSave = () => {
    dispatch(cancelStoreSecPicking());
  };

  if (!Object.keys(processingStoreSecPicking).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const successRows = processingStoreSecPicking.successRows;
    const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = [
      ...Object.keys(headers).map(
        (key) =>
          ({
            key,
            title: headers[key],
            dataIndex: key,
          } as any)
      ),
    ];
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ y: 500 }}
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Row>
                  <Col>
                    <Button
                      type="primary"
                      onClick={onSaveUserMapping}
                      loading={status === "loading"}
                      disabled={status === "loading"}
                    >
                      Save Store Secondary Picking
                    </Button>
                  </Col>
                  <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                    <Button
                      onClick={onCancelSave}
                      disabled={status === "loading"}
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Save Store Secondary Picking" />
        </Col>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        {!!(slots && processingStoreSecPicking.slot) && (
          <Descriptions
            size="small"
            layout="vertical"
            labelStyle={{ fontWeight: "bold" }}
            bordered
            column={{ xs: 2 }}
          >
            <Descriptions.Item label="Date">
              {processingStoreSecPicking.date}
            </Descriptions.Item>
            <Descriptions.Item label="Slot">
              {getSlotName(slots, processingStoreSecPicking.slot)}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Row>
      <Row>
        <Col xs={24}>{content}</Col>
      </Row>
    </>
  );
};
